import React, { useEffect, useState } from "react";
import { LinearProgress } from "@mui/material";
import { updateMainUser } from "../../services/patch";
import { createMainUser } from "../../services/post";
import { deleteMainUser } from "../../services/delete";
import GenericPanelToggle, { PanelProps, PanelType } from "../GenericPanelToggle";
import { getAllClients, getAllUsersWithClients } from "../../services/fetch";
import { useClientAccessContext } from "../../context/ClientAccessContext";
import { GlobalUserRole, UserWithClients } from "../../context/ClientAccessContext/types";
import MainUserForm, { MainUserFormProps } from "./MainUserForm";
import { AdminPanelClient } from "../TopicsPanel/types";

const MainUsersPanel = () => {
  const { userWithClients } = useClientAccessContext();
  const [allClients, setAllClients] = useState<AdminPanelClient[]>();
  const [users, setUsers] = useState<UserWithClients[]>();
  const [lastUnsavedId, setLastUnsavedId] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getAllUsersWithClients();
        setUsers(data.result);
      } catch (error) {
        console.error("Failed to fetch data for all main db users:", error);
      }
    };

    const fetchAllClients = async () => {
      try {
        const clientData = await getAllClients();
        setAllClients(clientData.result); // assuming you have a state variable called 'clients' to store the client data.
      } catch (error) {
        console.error("Failed to fetch all clients:", error);
      }
    };

    if (userWithClients.globalRole !== GlobalUserRole.ADMIN) {
      console.error(
        `User with id ${userWithClients.id} does not have access to the main users panel due to his global role of ${userWithClients.globalRole}`
      );
    } else {
      fetchData();
      fetchAllClients();
    }
  }, []);

  const updateUsersAndSelectedUser = (savedUserId: number, unsavedUser: UserWithClients) => {
    const idReference = unsavedUser.id || savedUserId;
    const savedUser = { ...unsavedUser, id: savedUserId };
    setUsers((prevUsers) => prevUsers.map((user) => (user.id === idReference ? savedUser : user)));
  };

  const handleSaveUser = async (updatedUser: UserWithClients) => {
    setIsLoading(true);
    if (updatedUser.id > 0) {
      const response = await updateMainUser(updatedUser.id, updatedUser);
      const savedUser = response.result.user;
      updateUsersAndSelectedUser(savedUser.id, updatedUser);
    } else {
      const response = await createMainUser(updatedUser);
      const savedUserId = response.result.mainDb.createdMainUserId;
      updateUsersAndSelectedUser(savedUserId, updatedUser);
    }
    setIsLoading(false);
  };

  const handleDeleteUser = async (id: number) => {
    setIsLoading(true);
    try {
      if (id > 0) await deleteMainUser(id);
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
    } catch (error) {
      console.error("Failed to delete user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewUser = () => {
    const unsavedUser: UserWithClients = {
      id: lastUnsavedId,
      lastName: "",
      firstName: "",
      email: "",
      adObjectId: "",
      globalRole: GlobalUserRole.READER,
      clientAccesses: [],
    };
    setLastUnsavedId(lastUnsavedId - 1);
    setUsers((prevUsers) => [unsavedUser, ...prevUsers]);
  };

  return (
    <>
      {!users?.length || isLoading ? (
        <LinearProgress />
      ) : (
        <GenericPanelToggle
          items={users}
          panelType={PanelType.MAIN_USER}
          handleAddNew={handleAddNewUser}
          RenderForm={(props: PanelProps) => {
            return (
              <MainUserForm
                {...(props as MainUserFormProps)}
                clients={allClients}
                onUserUpdate={handleSaveUser}
                onUserDelete={handleDeleteUser}
              />
            );
          }}
        />
      )}
    </>
  );
};

export default MainUsersPanel;
